
import React, { useState } from "react";
import { Navigation } from "@/components/layout/Navigation";
import { PaymentHistory } from "@/components/payments/PaymentHistory";
import { PaymentStatus } from "@/components/payments/PaymentStatus";
import { useProfileQuery } from "@/hooks/profile/useProfileQuery";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { LoadingState } from "@/components/payments/LoadingStates";
import { TutorialButton } from "@/components/intro/TutorialButton";
import { IntroTour } from "@/components/intro/IntroTour";
import { addDays, isAfter } from "date-fns";
import { UploadReceiptDialog } from "@/components/payments/UploadReceiptDialog";
import { PendingReceipts } from "@/components/payments/PendingReceipts";
import { DebtorsPanel } from "@/components/payments/DebtorsPanel";
import { useRecepcionistaRedirect } from "@/hooks/useRecepcionistaRedirect";

const VIKINGO_KEYWORD = "VIKINGO";

const Pagos = () => {
  const { data: profile, isLoading: profileLoading } = useProfileQuery();
  const { isRecepcionista } = useRecepcionistaRedirect();
  const [showTutorial, setShowTutorial] = useState(false);

  const { data: payments, isLoading: paymentsLoading } = useQuery({
    queryKey: ["payments", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("payments")
        .select("*")
        .eq("user_id", profile?.id)
        .order("paid_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!profile?.id,
  });

  const { data: gymData } = useQuery({
    queryKey: ["gym-name", profile?.gym_id],
    queryFn: async () => {
      const { data } = await supabase
        .from("gyms")
        .select("name")
        .eq("id", profile!.gym_id!)
        .single();
      return data;
    },
    enabled: !!profile?.gym_id,
  });

  const isVikingGym = gymData?.name?.toUpperCase().includes(VIKINGO_KEYWORD) ?? false;
  const isMastergym = gymData?.name?.toUpperCase().includes("MASTERGYM") ?? false;
  const canManageDebts = profile?.role === "admin" || profile?.role === "manager" || isRecepcionista === true;

  const lastPayment = payments?.[0];
  const paymentStatus = lastPayment 
    ? isAfter(addDays(new Date(lastPayment.paid_at), lastPayment.validity_days), new Date())
    : false;

  // Extract plan tier from description of last active payment
  const extractPlanName = (): string | undefined => {
    if (!paymentStatus || !lastPayment?.description) return undefined;
    const desc = lastPayment.description.toUpperCase();
    const tiers = ["BLACK", "PLATINUM", "GOLD", "SILVER"];
    for (const tier of tiers) {
      if (desc.includes(tier)) return tier;
    }
    return undefined;
  };

  const planName = extractPlanName();

  if (profileLoading || paymentsLoading) {
    return <LoadingState />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/10 via-background to-background text-primary pb-24">
      <TutorialButton onClick={() => setShowTutorial(true)} />
      <IntroTour run={showTutorial} setRun={setShowTutorial} />
      
      <main className="container">
        <header className="p-4 pt-16 sm:p-6 sm:pt-12 space-y-2 sm:space-y-4 text-center">
          <h1 className="text-xl sm:text-2xl font-heading font-bold text-primary">
            Estado de Membresía
          </h1>
          <p className="text-foreground text-sm">
            Revisa el estado de tu membresía y el historial de pagos
          </p>
        </header>
        
        <div className="space-y-6 animate-fade-in">
          <div className="p-8 bg-primary/10 space-y-6 payment-status">
            <PaymentStatus 
              paymentStatus={paymentStatus}
              lastPaymentDate={lastPayment?.paid_at}
              validityDays={lastPayment?.validity_days}
              planName={planName}
            />
          </div>

          {isVikingGym && profile?.gym_id && (
            <UploadReceiptDialog userId={profile.id} gymId={profile.gym_id} />
          )}

          {isMastergym && canManageDebts && profile?.gym_id && (
            <DebtorsPanel gymId={profile.gym_id} />
          )}

          {isVikingGym && <PendingReceipts userId={profile?.id} />}

          <PaymentHistory payments={payments || []} />
        </div>

        <Navigation />
      </main>
    </div>
  );
};

export default Pagos;
